/* global HTMLDivElement, MouseEvent, Node */
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { ChangeEvent, InputHTMLAttributes, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

const COLOR_PRESETS = [
  { value: "#7c3aed", label: "Tím thương hiệu" },
  { value: "#3b82f6", label: "Xanh dương" },
  { value: "#06b6d4", label: "Cyan" },
  { value: "#10b981", label: "Xanh lá" },
  { value: "#f59e0b", label: "Vàng" },
  { value: "#f43f5e", label: "Hồng đỏ" },
  { value: "#ef4444", label: "Đỏ" },
  { value: "#8b5cf6", label: "Tím sáng" },
  { value: "#64748b", label: "Xám" },
  { value: "#111827", label: "Đen mực" },
] as const;

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

function normalizeHexColor(value: unknown, fallback = "#7c3aed") {
  const raw = String(value ?? "").trim();
  return HEX_COLOR_RE.test(raw) ? raw.toLowerCase() : fallback;
}

function colorLabel(value: string) {
  return COLOR_PRESETS.find((preset) => preset.value === value)?.label ?? value.toUpperCase();
}

/** Shared bento custom color picker: native color input is kept hidden for form contract compatibility. */
export function BentoColorInput({ wrapClassName = "", className = "", ...props }: InputHTMLAttributes<HTMLInputElement> & { wrapClassName?: string }) {
  const {
    id,
    value,
    defaultValue,
    onChange,
    disabled,
    name,
    required,
    ...inputProps
  } = props;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() => normalizeHexColor(defaultValue));
  const currentValue = normalizeHexColor(isControlled ? value : internalValue);
  const generatedId = useId();
  const panelId = `${id ?? generatedId}-color-panel`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(currentValue.toUpperCase());
  const [panelPosition, setPanelPosition] = useState({ left: 0, top: 0, width: 280 });

  useEffect(() => {
    if (open) {
      setDraft(currentValue.toUpperCase());
    }
  }, [currentValue, open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const positionPanel = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 8;
      const panelWidth = Math.min(Math.max(rect.width, 280), Math.max(260, window.innerWidth - 16));
      const panelHeight = 256;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = spaceBelow < panelHeight && spaceAbove > spaceBelow;
      setPanelPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - panelWidth - 8)),
        top: openUp ? Math.max(8, rect.top - panelHeight - gap) : Math.min(rect.bottom + gap, window.innerHeight - panelHeight - 8),
        width: panelWidth,
      });
    };
    positionPanel();
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open]);

  function emitChange(nextValue: string) {
    const normalized = normalizeHexColor(nextValue);
    if (!isControlled) {
      setInternalValue(normalized);
    }
    if (nativeRef.current) {
      nativeRef.current.value = normalized;
      onChange?.({
        target: nativeRef.current,
        currentTarget: nativeRef.current,
      } as ChangeEvent<HTMLInputElement>);
    }
  }

  function choose(nextValue: string) {
    if (disabled) return;
    const normalized = normalizeHexColor(nextValue);
    setDraft(normalized.toUpperCase());
    emitChange(normalized);
  }

  function moveColor(direction: 1 | -1) {
    const currentIndex = COLOR_PRESETS.findIndex((preset) => preset.value === currentValue);
    const start = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (start + direction + COLOR_PRESETS.length) % COLOR_PRESETS.length;
    choose(COLOR_PRESETS[nextIndex].value);
    setOpen(true);
  }

  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((next) => !next);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      moveColor(1);
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      moveColor(-1);
    }
  }

  function handleNativeChange(event: ChangeEvent<HTMLInputElement>) {
    const next = normalizeHexColor(event.target.value);
    if (!isControlled) {
      setInternalValue(next);
    }
    onChange?.(event);
  }

  function handleDraftChange(nextDraft: string) {
    const normalizedDraft = nextDraft.startsWith("#") ? nextDraft : `#${nextDraft}`;
    setDraft(normalizedDraft.toUpperCase());
    if (HEX_COLOR_RE.test(normalizedDraft)) {
      emitChange(normalizedDraft);
    }
  }

  const draftValid = HEX_COLOR_RE.test(draft);

  return (
    <div ref={rootRef} className={`group relative ${wrapClassName}`}>
      <input
        {...inputProps}
        ref={nativeRef}
        id={id}
        name={name}
        required={required}
        disabled={disabled}
        type="color"
        value={currentValue}
        onChange={handleNativeChange}
        onFocus={() => triggerRef.current?.focus()}
        tabIndex={-1}
        className="sr-only"
      />
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((next) => !next);
        }}
        onKeyDown={handleTriggerKeyDown}
        className={`flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-ink-200 bg-white/80 px-3.5 py-2.5 pr-2 text-left text-[13px] font-semibold text-ink-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] outline-none transition duration-150 hover:border-ink-300 hover:bg-white focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-100/70 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="h-5 w-5 shrink-0 rounded-lg border border-white shadow-sm ring-1 ring-ink-200" style={{ backgroundColor: currentValue }} />
          <span className="min-w-0 flex-1 truncate">{colorLabel(currentValue)}</span>
          <span className="hidden shrink-0 font-mono text-[11px] font-bold text-ink-400 sm:inline">{currentValue.toUpperCase()}</span>
        </span>
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white text-ink-500 shadow-sm ring-1 ring-ink-200 transition group-hover:text-brand-600 group-focus-within:bg-brand-50 group-focus-within:text-brand-600 group-focus-within:ring-brand-100">
          <ChevronDown className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="Chọn màu"
          style={{
            left: panelPosition.left,
            top: panelPosition.top,
            width: panelPosition.width,
          }}
          className="fixed z-[90] rounded-2xl border border-ink-200 bg-white p-3 shadow-[0_24px_70px_rgba(31,29,43,0.2)] ring-1 ring-white/70"
        >
          <div className="grid grid-cols-5 gap-2">
            {COLOR_PRESETS.map((preset) => {
              const isSelected = preset.value === currentValue;
              return (
                <button
                  key={preset.value}
                  type="button"
                  aria-label={preset.label}
                  aria-pressed={isSelected}
                  onClick={() => choose(preset.value)}
                  className={`grid h-10 place-items-center rounded-xl border transition ${
                    isSelected ? "border-brand-500 bg-brand-50 shadow-sm ring-4 ring-brand-100/70" : "border-ink-200 bg-white hover:border-ink-300 hover:bg-ink-50"
                  }`}
                >
                  <span className="grid h-6 w-6 place-items-center rounded-lg border border-white shadow-sm ring-1 ring-black/10" style={{ backgroundColor: preset.value }}>
                    {isSelected && <Check className="h-3.5 w-3.5 text-white drop-shadow" />}
                  </span>
                </button>
              );
            })}
          </div>
          <label className="mt-3 flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-400">Mã màu</span>
            <div className={`flex items-center gap-2 rounded-xl border bg-ink-50 px-3 py-2 transition ${draftValid ? "border-ink-200 focus-within:border-brand-400 focus-within:bg-white" : "border-rose-200 bg-rose-50"}`}>
              <span className="h-5 w-5 shrink-0 rounded-lg border border-white shadow-sm ring-1 ring-ink-200" style={{ backgroundColor: draftValid ? draft : currentValue }} />
              <input
                value={draft}
                onChange={(event) => handleDraftChange(event.target.value)}
                onFocus={(event) => event.currentTarget.select()}
                className="min-w-0 flex-1 bg-transparent font-mono text-[13px] font-bold text-ink-800 outline-none"
                placeholder="#7C3AED"
                spellCheck={false}
              />
            </div>
          </label>
          <div className="mt-3 flex items-center justify-between border-t border-ink-100 pt-3">
            <button
              type="button"
              onClick={() => choose("#7c3aed")}
              className="rounded-lg bg-brand-50 px-3 py-1.5 text-[12px] font-bold text-brand-600 transition hover:bg-brand-100"
            >
              Mặc định
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                triggerRef.current?.focus();
              }}
              className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-ink-500 transition hover:bg-ink-100 hover:text-ink-800"
            >
              Xong
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
